import { AuditLogRow } from '@/features/AuditLogs/components/AuditLogsTable/AuditLogRow'
import {
  type AuditLogTableProps,
  AuditLogTableShell,
} from '@/features/AuditLogs/components/AuditLogsTable/AuditLogTableShell'
import { Table } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'

/**
 * One width per column of the header row below, in the same order: status
 * indicator, timestamp, user, action, resource. It lives here rather than with
 * the caller so a column cannot be added without the loading skeleton gaining a
 * cell for it — the widths and the columns they stand in for stay in one file.
 */
export const AUDIT_LOGS_SKELETON_WIDTHS = [150, 200, 300, 260, 100]

export function AuditLogsTable({
  logs,
  isLoading,
  isError,
  isEmpty,
  skeletonWidths,
  selectedLogId,
  onRowClick,
  errorComponent,
  emptyMessage,
}: Readonly<AuditLogTableProps>) {
  const { t } = useTranslation('features', { useSuspense: false })

  return (
    <AuditLogTableShell
      logs={logs}
      isLoading={isLoading}
      isError={isError}
      isEmpty={isEmpty}
      skeletonWidths={skeletonWidths}
      errorComponent={errorComponent}
      emptyMessage={emptyMessage}
      // status indicator + timestamp + user + action + resource
      columnCount={5}
      ariaLabel="Audit logs table"
      headerRow={
        <Table.Row>
          {/* Status indicator column - no header */}
          <Table.ColumnHeader width="4px" p={0} />
          <Table.ColumnHeader whiteSpace="nowrap" width="auto">
            {t('auditLogs.table.columns.timestamp')}
          </Table.ColumnHeader>
          <Table.ColumnHeader width="220px">
            {t('auditLogs.table.columns.user')}
          </Table.ColumnHeader>
          {/* Between who and what: the row reads "who did what, to which
              thing". Without it the table answered only the first and last,
              and the middle needed the detail panel opening. */}
          <Table.ColumnHeader width="260px">
            {t('auditLogs.table.columns.action')}
          </Table.ColumnHeader>
          <Table.ColumnHeader>
            {t('auditLogs.table.columns.resource')}
          </Table.ColumnHeader>
        </Table.Row>
      }
      renderRow={(log, showDate) => (
        <AuditLogRow
          log={log}
          isSelected={log.id === selectedLogId}
          onClick={() => onRowClick(log)}
          showDate={showDate}
        />
      )}
    />
  )
}
